<template>
  <div class="video-field" :aria-busy="busy">
    <div class="video-field__heading"><strong>{{ label }}</strong><span>{{ modelValue.length }} / {{ maxVideos }}</span></div>
    <p class="video-field__hint">MP4, WebM o MOV · Hasta 500 MB por video. Podés previsualizar, ordenar y agregar una descripción.</p>
    <div class="video-field__list">
      <div v-for="(video, index) in modelValue" :key="`${video.url}-${index}`" class="video-field__item">
        <VideoPreview :src="video.url" :poster="video.poster" :title="video.description || `Video ${index + 1}`" />
        <label class="video-field__description">Descripción del video {{ index + 1 }}
          <input :value="video.description" maxlength="500" :disabled="disabled || busy" class="form-control"
            placeholder="Breve descripción (opcional)" @input="describe(index, $event.target.value)" />
        </label>
        <div class="video-field__actions">
          <button type="button" :disabled="disabled || busy || index === 0" :aria-label="`Mover video ${index + 1} antes`" @click="move(index, -1)">↑</button>
          <button type="button" :disabled="disabled || busy || index === modelValue.length - 1" :aria-label="`Mover video ${index + 1} después`" @click="move(index, 1)">↓</button>
          <button type="button" :disabled="disabled || busy" @click="remove(index)">Quitar video</button>
        </div>
      </div>
    </div>
    <div v-if="localUrl" class="video-field__pending">
      <VideoPreview :src="localUrl" title="Vista previa del archivo seleccionado" />
      <p class="video-field__filename">{{ pendingFile?.name }}</p>
      <progress v-if="busy" :value="progress" max="100" aria-label="Progreso de carga del video" />
      <p role="status">{{ busy ? (progress === 100 ? 'Finalizando carga…' : `Subiendo video… ${progress}%`) : 'Vista previa local · Todavía no se agregó al contenido.' }}</p>
      <div class="video-field__actions">
        <button v-if="!busy" type="button" :disabled="disabled" @click="startUpload">{{ upload ? 'Reintentar carga' : 'Subir video' }}</button>
        <button type="button" @click="cancelUpload">Cancelar</button>
      </div>
    </div>
    <button v-else-if="modelValue.length < maxVideos" class="video-field__add" type="button" :disabled="disabled" @click="input?.click()">
      <i class="fas fa-video" aria-hidden="true"></i> Agregar video
    </button>
    <input ref="input" type="file" accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov" hidden @change="selectFile" />
    <p v-if="error" class="video-field__error" role="alert">{{ error }}</p>
    <p v-if="notice" class="video-field__hint" role="status">{{ notice }}</p>
  </div>
</template>

<script setup>
import { ref, onBeforeUnmount } from 'vue';
import VideoPreview from './VideoPreview.vue';
import { createVideoUpload, videoFileError } from '@/services/video';
const props = defineProps({ modelValue: { type: Array, default: () => [] }, label: { type: String, default: 'Videos' }, disabled: Boolean, maxVideos: { type: Number, default: 12 } });
const emit = defineEmits(['update:modelValue', 'busy']);
const input = ref(null), pendingFile = ref(null), localUrl = ref(''), progress = ref(0), busy = ref(false), error = ref(''), notice = ref('');
let upload = null;
let generation = 0;
function cleanup() {
  if (localUrl.value) URL.revokeObjectURL(localUrl.value);
  localUrl.value = ''; pendingFile.value = null;
}
function selectFile(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;
  if (props.modelValue.length >= props.maxVideos) { error.value = `Podés agregar hasta ${props.maxVideos} videos.`; return; }
  error.value = videoFileError(file);
  if (error.value) return;
  cleanup(); upload = null; notice.value = ''; progress.value = 0;
  pendingFile.value = file; localUrl.value = URL.createObjectURL(file);
  // Prevent saving a form while a selected file is still local or failed.
  emit('busy', true);
}
function setBusy(value) { busy.value = value; }
async function startUpload() {
  if (busy.value || !pendingFile.value) return;
  setBusy(true); error.value = '';
  const current = generation;
  try {
    if (!upload) {
      const task = await createVideoUpload(pendingFile.value, {
        onProgress: (value) => { if (current === generation) progress.value = value; },
        onError: (message) => { if (current === generation) { error.value = message; setBusy(false); } },
        onSuccess: (video) => {
          if (current !== generation) return;
          emit('update:modelValue', [...props.modelValue, video]);
          notice.value = 'Video cargado. Bunny lo está procesando; la reproducción estará disponible al terminar. Guardá los cambios para publicarlo.';
          cleanup(); upload = null; setBusy(false); emit('busy', false);
        },
      });
      if (current !== generation) { await task.abort(); return; }
      upload = task;
    }
    upload.start();
  } catch (e) {
    if (current !== generation) return;
    error.value = e.response?.data?.detail || 'No se pudo iniciar la carga. Revisá la conexión e intentá nuevamente.';
    setBusy(false);
  }
}
function cancelUpload() {
  generation += 1;
  if (upload) upload.abort().catch(() => {});
  upload = null; cleanup(); setBusy(false); emit('busy', false); error.value = '';
}
function describe(index, description) { emit('update:modelValue', props.modelValue.map((v, i) => i === index ? { ...v, description } : v)); }
function remove(index) { emit('update:modelValue', props.modelValue.filter((_, i) => i !== index)); }
function move(index, direction) {
  const next = [...props.modelValue];
  [next[index], next[index + direction]] = [next[index + direction], next[index]];
  emit('update:modelValue', next);
}
onBeforeUnmount(cancelUpload);
</script>

<style scoped>
.video-field { padding: 20px; border: 1px solid #ffffff26; border-radius: 12px; color: inherit; min-width: 0; }
.video-field__heading, .video-field__actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.video-field__heading { justify-content: space-between; }
.video-field__hint { color: #bbb; font-size: .9rem; margin: 10px 0; }
.video-field__list { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr)); gap: 20px; }
.video-field__item { min-width: 0; margin: 16px 0; }
.video-field__pending { max-width: 640px; margin: 16px 0; }
.video-field__description { display: block; font-size: .9rem; margin: 10px 0; }
.video-field__description input { margin-top: 6px; background: #202020; color: #fff; border-color: #ffffff45; }
.video-field__description input::placeholder { color: #aaa; }
.video-field button { background: #272727; color: white; border: 1px solid #ffffff45; border-radius: 6px; padding: 10px 14px; min-height: 44px; }
.video-field button:hover:not(:disabled), .video-field button:focus-visible { border-color: var(--primary-color, #ffe000); }
.video-field button:disabled { opacity: .4; }
.video-field__add { width: 100%; border-style: dashed !important; }
.video-field__filename { overflow-wrap: anywhere; margin: 10px 0; }
.video-field progress { width: 100%; accent-color: var(--primary-color, #ffe000); }
.video-field__error { color: #ffb4b4; margin: 12px 0 0; }
</style>
